import React from "react";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { pickLocalized } from "@/lib/i18n";
// Relative: resolvable under Jest without a shim.
import { PLATFORM_OFFERS as IDS } from "../../constants/testIds/platformOffers";
import { formatMoneyIntl } from "../../lib/formatMoney";
import { offerSummary, perInvoiceText } from "../../platformOffers/describe";

/**
 * Adapts the wire shape's { <field>, <field>_i18n: {fr, es} } pair (feature_rows' `label`
 * / `label_i18n` — contract §3.3) to the flat "_en"/"_fr"/"_es" suffix shape
 * `pickLocalized` (frontend/src/i18n/pickLocalized.js) already knows how to resolve, with
 * the canonical value written under "_en" so an "en" reader resolves to it directly
 * rather than falling through to a French override that happens to be present.
 */
function localize(canonical, i18nOverlay, field, lang) {
  return pickLocalized(
    { [`${field}_en`]: canonical, [`${field}_fr`]: i18nOverlay?.fr, [`${field}_es`]: i18nOverlay?.es },
    field,
    lang,
  );
}

/**
 * SignupSummary — carte récapitulative du plan/devise/facturation (aside du Signup).
 * Présentationnel : reçoit le plan sélectionné + le formulaire.
 *
 * `offers` are the applied platform codes (one §5.1 preview per slot). Neither touches
 * the trial: a promo is attached when the trial ends and discounts that many PAID
 * invoices (contract §5.0), and a gift card's credit is drawn from the first paid
 * invoices. `trialDays` is `null` until `/saas/config` resolves, and the sentence then
 * says "after your trial" without a number rather than an invented one.
 */
export const SignupSummary = ({ selectedPlan, form, offers, trialDays }) => {
  const t = useT("saas");
  const tc = useT("common");
  const { lang } = useLanguage();
  const known = trialDays != null;
  return (
  <aside className="lg:col-span-2">
    <div className="sticky top-8 rounded-3xl border border-white/10 bg-white/[0.02] p-8" data-testid="signup-summary">
      <h3 className="font-sol font-semibold text-xl mb-6">{t("signup.summaryTitle")}</h3>
      {selectedPlan && (
        <>
          <div className="flex justify-between items-baseline mb-4">
            <span className="text-white/60">{t("signup.plan")}</span>
            <span className="font-medium">{selectedPlan.name}</span>
          </div>
          <div className="flex justify-between items-baseline mb-6">
            <span className="text-white/60">{t("signup.currency")}</span>
            <span className="font-medium">{form.currency}</span>
          </div>
          <div className="border-t border-white/10 pt-6 mb-6">
            {form.billing_cycle === "annual" ? (
              <>
                <div className="flex justify-between items-baseline">
                  <span className="text-white/60">{t("signup.summaryAnnual")}</span>
                  <span className="font-sol font-semibold text-3xl">
                    {selectedPlan.annual_prices?.[form.currency] || (selectedPlan.prices?.[form.currency] || 0) * 10} <span className="text-base text-white/40">{form.currency}</span>
                  </span>
                </div>
                <div className="mt-2 inline-block text-xs text-sol-gold bg-sol-gold/10 px-2 py-1 rounded-full" data-testid="signup-annual-savings">
                  {t("signup.summaryAnnualSavings")}
                </div>
              </>
            ) : (
              <div className="flex justify-between items-baseline">
                <span className="text-white/60">{t("signup.billingMonthly")}</span>
                <span className="font-sol font-semibold text-3xl">
                  {selectedPlan.prices?.[form.currency] || 0} <span className="text-base text-white/40">{form.currency}</span>
                </span>
              </div>
            )}
          </div>
          {offers.promo && (
            <div
              className="mb-4 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-4 text-sm"
              data-testid={IDS.signupSummaryDiscount}
            >
              <div className="flex justify-between items-baseline gap-3">
                <span className="font-medium whitespace-nowrap">{offers.promo.code}</span>
                <span className="text-emerald-300 text-right">{offerSummary(offers.promo, tc, lang)}</span>
              </div>
              <p className="mt-1 text-white/60">{perInvoiceText(offers.promo, tc, lang)}</p>
              <p className="mt-1 text-xs text-white/40">{known
                  ? t("signup.summary.discountStartsDays", { days: trialDays })
                  : t("signup.summary.discountStarts")}</p>
            </div>
          )}
          {offers.giftcard && (
            <div
              className="mb-6 rounded-2xl border border-sol-gold/30 bg-sol-gold/10 p-4 text-sm"
              data-testid={IDS.signupSummaryCredit}
            >
              <div className="flex justify-between items-baseline gap-3">
                <span className="font-medium whitespace-nowrap">{offers.giftcard.code}</span>
                <span className="text-sol-gold">
                  {formatMoneyIntl(offers.giftcard.credit_cents, offers.giftcard.currency, lang)}
                </span>
              </div>
              <p className="mt-1 text-xs text-white/40">{known
                  ? t("signup.summary.creditStartsDays", { days: trialDays })
                  : t("signup.summary.creditStarts")}</p>
            </div>
          )}
          <ul className="space-y-2 text-sm text-white/60">
            {(selectedPlan.feature_rows || []).slice(0, 5).map((row) => {
              const label = localize(row.label, row.label_i18n, "label", lang);
              const display = row.kind === "limit"
                ? (row.value === -1
                    ? `${label} — ${t("pricing.unlimited")}`
                    : (row.label_template || label).replace(/\{value\}/g, String(row.value)))
                : label;
              const suffix = row.status === "coming_soon" ? ` (${t("pricing.comingSoon")})` : "";
              return <li key={row.key}>· {display}{suffix}</li>;
            })}
          </ul>
        </>
      )}
    </div>
  </aside>
  );
};

export default SignupSummary;
